import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Switch from "@mui/material/Switch";
import LinesAutocomplete from "./lines-autocomplete";
import StopsAutocomplete from "./stops-autocomplete";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
// eslint-disable-next-line
import InfoIcon from "@mui/icons-material/Info";
import DrawerIconLoader from "../shared/drawer-icon-loader";
import { useSelector, useDispatch } from "react-redux";
import {
  setNavTram,
  setNavTrolley,
  setNavBus,
  setNavSubway,
  setNavHobo,
  setNavSpark,
  setNavCharger,
  setNavCycleways,
  setNavGreenCorridor1,
  setNavGreenCorridor2,
  setNavGreenCorridor3,
  setNavReconstructionsPermanent,
  setNavReconstructionsTemporary,
} from "../../redux/actions/navigation-actions";
import { setSelectedLine } from "../../redux/actions/shared-actions";
import { setTBStaticFilteredData } from "../../redux/actions/static-data-actions";
import { setTMStaticFilteredData } from "../../redux/actions/static-data-actions";
import { setBStaticFilteredData } from "../../redux/actions/static-data-actions";

/**
 * Based on the clicked navigation layer is displayed a drawer component emerging from the
 * right of the web page.
 * @component
 * @category Components
 * @subcategory Navigation
 * @param {function} drawerOnClose  handles the closing of the drawer
 * @param {string} title the title of the drawer/layer
 * @param {object} layer holds the layer data for the specific drawer
 */

const NavigationDrawer = ({ drawerOnClose, title, layer }) => {
  const { t } = useTranslation();
  const drawerWidth = "300px";
  const dispatch = useDispatch();
  const { navigation, selectedLine, isDrawerOpen, websocket, staticData } =
    useSelector((state) => ({
      navigation: state.navigation,
      selectedLine: state.shared.selectedLine,
      isDrawerOpen: state.shared.isDrawerOpen,
      websocket: state.websocket,
      staticData: state.staticData,
    }));

  const navStateSetters = {
    tram: (value) => dispatch(setNavTram(value)),
    trolley: (value) => dispatch(setNavTrolley(value)),
    bus: (value) => dispatch(setNavBus(value)),
    subwayStations: (value) => dispatch(setNavSubway(value)),
    hobo: (value) => dispatch(setNavHobo(value)),
    spark: (value) => dispatch(setNavSpark(value)),
    charger: (value) => dispatch(setNavCharger(value)),
    cycleways: (value) => dispatch(setNavCycleways(value)),
    greenCorridor1: (value) => dispatch(setNavGreenCorridor1(value)),
    greenCorridor2: (value) => dispatch(setNavGreenCorridor2(value)),
    greenCorridor3: (value) => dispatch(setNavGreenCorridor3(value)),
    //kanachkova
    reconstructionsTemporary: (value) =>
      dispatch(setNavReconstructionsTemporary(value)),
    reconstructionsPermanent: (value) =>
      dispatch(setNavReconstructionsPermanent(value)),
  };

  const [checked, setChecked] = React.useState(
    Object.keys(navigation).filter((x) => navigation[x].isActive)
  );
  const [lineSearchToggle, setLineSearchToggle] = React.useState({
    tram: false,
    trolley: false,
    bus: false,
  });

  const handleLineSearchToggle = (type) => {
    if (type === "tram" && lineSearchToggle.tram) {
      setLineSearchToggle({ tram: false, trolley: false, bus: false });
      dispatch(setSelectedLine(null));
    }
    if (type === "tram" && !lineSearchToggle.tram) {
      setLineSearchToggle({ tram: true, trolley: false, bus: false });
      navStateSetters[type](true);
    }
    if (type === "trolley" && lineSearchToggle.trolley) {
      setLineSearchToggle({ tram: false, trolley: false, bus: false });
      dispatch(setSelectedLine(null));
    }
    if (type === "trolley" && !lineSearchToggle.trolley) {
      setLineSearchToggle({ tram: false, trolley: true, bus: false });
      navStateSetters[type](true);
    }
    if (type === "bus" && lineSearchToggle.bus) {
      setLineSearchToggle({ tram: false, trolley: false, bus: false });
      dispatch(setSelectedLine(null));
    }
    if (type === "bus" && !lineSearchToggle.bus) {
      setLineSearchToggle({ tram: false, trolley: false, bus: true });
      navStateSetters[type](true);
    }
  };

  const greenCorridorsToggle = () => {
    let lines = {
      tramLines: [],
      trolleyLines: [],
      busLines: [],
    };
    for (
      let counter = 1;
      counter <=
      Object.keys(navigation).filter((x) => x.includes("greenCorridor")).length;
      counter++
    ) {
      if (navigation[`greenCorridor${counter}`].isActive) {
        const tramLines = [
          ...staticData[`greenCorridor${counter}`][0].tramLines,
        ];
        const trolleyLines =
          staticData[`greenCorridor${counter}`][0].trolleyLines;
        const busLines = staticData[`greenCorridor${counter}`][0].busLines;

        tramLines.forEach((tramLine) => {
          if (lines.tramLines.indexOf(tramLine) === -1) {
            lines.tramLines.push(tramLine);
          }
        });
        trolleyLines.forEach((trolleyLine) => {
          if (lines.trolleyLines.indexOf(trolleyLine) === -1) {
            lines.trolleyLines.push(trolleyLine);
          }
        });
        busLines.forEach((busLine) => {
          if (lines.busLines.indexOf(busLine) === -1) {
            lines.busLines.push(busLine);
          }
        });
      }
    }

    dispatch(
      setTBStaticFilteredData(
        staticData.TBData.filter((x) => lines.trolleyLines.indexOf(x.line) > -1)
      )
    );
    dispatch(
      setTMStaticFilteredData(
        staticData.TMData.filter((x) => lines.tramLines.indexOf(x.line) > -1)
      )
    );
    dispatch(
      setBStaticFilteredData(
        staticData.BData.filter((x) => lines.busLines.indexOf(x.line) > -1)
      )
    );
  };

  useEffect(() => {
    if (title === "GREEN_INFRASTRUCTURE") {
      greenCorridorsToggle();
      return () => {
        dispatch(setTBStaticFilteredData(staticData.TBData));
        dispatch(setTMStaticFilteredData(staticData.TMData));
        dispatch(setBStaticFilteredData(staticData.BData));
      };
    }
    // eslint-disable-next-line
  }, [navigation]);

  const handleToggle = (value) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];
    if (currentIndex === -1) {
      navStateSetters[value](true);
      newChecked.push(value);
    } else {
      navStateSetters[value](false);
      newChecked.splice(currentIndex, 1);
      if (value === "trolley") {
        if (lineSearchToggle.trolley) {
          setLineSearchToggle({ ...lineSearchToggle, trolley: false });
        }
      }
      if (value === "tram") {
        if (lineSearchToggle.tram) {
          setLineSearchToggle({ ...lineSearchToggle, tram: false });
        }
      }
      if (value === "bus") {
        if (lineSearchToggle.bus) {
          setLineSearchToggle({ ...lineSearchToggle, bus: false });
        }
      }
    }
    dispatch(setSelectedLine(null));
    setLineSearchToggle({ tram: false, trolley: false, bus: false });
    setChecked(newChecked);
  };

  React.useEffect(() => {
    setChecked(Object.keys(navigation).filter((x) => navigation[x].isActive));
    if (!layer.tram) {
      setLineSearchToggle({ tram: false, trolley: false, bus: false });
    }
  }, [layer, selectedLine, navigation]);

  // React.useEffect(() => {}, [websocket]);

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
          },
        }}
        className="drawer-right"
        variant="persistent"
        anchor="right"
        open={isDrawerOpen}
      >
        <List>
          {title === "PUBLIC_TRANSIT" ? <StopsAutocomplete /> : null}
          {Object.entries(layer).map(([type, { iconURL, title }], index) => {
            return (
              <div key={type}>
                <ListItem>
                  {!!iconURL ? (
                    <ListItemIcon>
                      <img src={iconURL} width={50} height={50} alt="" />
                    </ListItemIcon>
                  ) : null}
                  <div
                    className={
                      type === "tram" || type === "trolley" || type === "bus"
                        ? "line-search"
                        : ""
                    }
                    style={{
                      display: "flex",
                      flexDrection: "row",
                      alignItems: "center",
                      maxWidth: "80px",
                      textAlign: "left",
                    }}
                    onClick={() => handleLineSearchToggle(type)}
                  >
                    <p>{t(title)}</p>
                    {type === "tram" || type === "trolley" || type === "bus" ? (
                      <>
                        {!lineSearchToggle[type] ? (
                          <ExpandMoreIcon
                            onMouseOver={(e) => {
                              e.target.style.cursor = "pointer";
                            }}
                          />
                        ) : (
                          <ExpandLessIcon
                            onMouseOver={(e) => {
                              e.target.style.cursor = "pointer";
                            }}
                          />
                        )}
                      </>
                    ) : null}
                  </div>

                  <Switch
                    edge="end"
                    onChange={handleToggle(type)}
                    checked={checked.indexOf(type) !== -1}
                    inputProps={{
                      "aria-labelledby": `switch-list-label-${type}`,
                    }}
                  />
                  {type === "hobo" && !Object.keys(websocket.hobo).length ? (
                    <DrawerIconLoader />
                  ) : null}
                  {type === "spark" && !Object.keys(websocket.spark).length ? (
                    <DrawerIconLoader />
                  ) : null}
                  {type === "bus" &&
                  !Object.keys(websocket.avl.buses).length ? (
                    <DrawerIconLoader />
                  ) : null}
                  {type === "tram" &&
                  !Object.keys(websocket.avl.trams).length ? (
                    <DrawerIconLoader />
                  ) : null}
                  {type === "trolley" &&
                  !Object.keys(websocket.avl.trolleysAndEBuses).length ? (
                    <DrawerIconLoader />
                  ) : null}
                </ListItem>
                {lineSearchToggle.bus && index === 0 ? (
                  <LinesAutocomplete label={"BUSES"} />
                ) : null}
                {lineSearchToggle.tram && index === 1 ? (
                  <LinesAutocomplete label={"TRAMS"} />
                ) : null}
                {lineSearchToggle.trolley && index === 2 ? (
                  <LinesAutocomplete label={"TROLLEYS_AND_EBUSES"} />
                ) : null}
              </div>
            );
          })}
        </List>
        <Divider />
        {/* <div className='drawer-info'>
          <div className='info-icon-container'>
            <InfoIcon />
          </div>
          <Box>
            <div className='info-text'>{t(`${title}_DRAWER_INFO`)}</div>
          </Box>
        </div> */}
      </Drawer>
    </Box>
  );
};
export default React.memo(NavigationDrawer);
